/**
 * MoveDocumentModal - Modal for moving a document to a different project.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FolderOpen, FolderInput, Loader2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui';
import type { SavedDocument, ProjectConfig } from '@/lib/types';

interface MoveDocumentModalProps {
  document: SavedDocument;
  projects: ProjectConfig[];
  isMoving: boolean;
  onMove: (documentId: string, projectId: string, projectName: string) => void;
  onClose: () => void;
}

export const MoveDocumentModal: React.FC<MoveDocumentModalProps> = ({
  document: doc,
  projects,
  isMoving,
  onMove,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Filter out the current project
  const availableProjects = projects.filter((p) => p.id !== doc.projectId);

  const handleMove = () => {
    if (!selectedProjectId) return;
    const project = projects.find((p) => p.id === selectedProjectId);
    if (project) {
      onMove(doc.id, project.id, project.projectName);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <FolderInput className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              {t('projects.moveDocumentTitle')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label={t('projects.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-sm text-slate-500 mb-4">
            {t('projects.moveDocumentDescription', { title: doc.title })}
          </p>

          {availableProjects.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {t('projects.noOtherProjects')}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {availableProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => setSelectedProjectId(project.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                    selectedProjectId === project.id
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <FolderOpen className={`w-4.5 h-4.5 flex-shrink-0 ${
                    selectedProjectId === project.id ? 'text-indigo-600' : 'text-slate-400'
                  }`} />
                  <span className="flex-1 text-sm font-medium truncate">
                    {project.projectName}
                  </span>
                  {selectedProjectId === project.id && (
                    <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isMoving}>
            {t('projects.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleMove}
            disabled={!selectedProjectId || isMoving}
          >
            {isMoving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('projects.moving')}
              </>
            ) : (
              <>
                <FolderInput className="w-4 h-4" />
                {t('projects.moveHere')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MoveDocumentModal;
